import { Component, computed, OnDestroy, OnInit } from '@angular/core';
import { UserService } from '@/core/services/open-api/user.service';
import { filter, startWith, Subscription } from 'rxjs';
import { CurrentUser } from '@/core/models/user';
import { LayerService } from '@/core/services/open-api/layer.service';
import { TranslateService } from '@ngx-translate/core';
import { AngularTranslations } from '@/core/types/angular-primeng-api';
import { Layer } from '@/core/models/layer';
import { UserPermission } from '@/core/models/user-permission';
import { OnboardingProgressBarComponent } from '@/home/onboarding-progress-bar.component';
import { TopBarControlsComponent } from '@/home/top-bar-controls.component';
import { LayoutService } from '@/theme/service/layout.service';
import { TopBarNewsComponent } from '@/home/top-bar-news.component';
import { MenuItemEntry, MenuItemType } from '@/theme/types/theme.types';
import { NavigationEnd, Router } from '@angular/router';
import { TtlCacheStore } from '@/core/utils/cache/ttl-cache-store';
import { InitializationManager } from '@/core/utils/initialization-manager';
import { LoadingService } from '@/core/services/loading.service';
import { Title } from '@angular/platform-browser';

export type MenuKey = 'main' | 'settings';

@Component({
    selector: 'app-web-console',
    standalone: false,
    template: `<app-layout
            [menuData]="menuData"
            [menuBackLink]="menuBackLink"
            logoAlt="timeBuzzer"
            logoSrc="assets/images/timeBuzzer-logo-white.svg"
            [logoMobileSrc]="logoMobileSrc"
            [onLogoClick]="onLogoClick"
            title=""
            [beforeMenuSlot]="onboardingProgressbar"
            [displaySearch]="false"
            [userName]="userName"
            [profileMenuData]="profileMenuData"
            [topBarSlotTwo]="topBarControls"
            [footerCopyright]="'&#169; ' + currentYear + ' timeBuzzer® GmbH'"
            [footerLogoTemplate]="footerLogoTemplate"
            [beforeTopBarSlot]="topbarNewsComponent"
        >
        </app-layout>
        <ng-template #footerLogoTemplate>
            <img src="/assets/images/logo-{{ isDarkTheme() ? 'white' : 'dark' }}.svg" alt="" />
            <span class="footer-app-name">timeBuzzer | {{ 'standard_console' | translate }}</span>
        </ng-template> `,
    // template: `<app-home />`,
})
export class WebConsoleComponent implements OnInit, OnDestroy {
    subscriptions = new Subscription();
    currentUser?: CurrentUser;
    layers?: Layer[];
    menuData: MenuItemType[] = [];
    profileMenuData: MenuItemType[] = [];
    onboardingProgressbar = OnboardingProgressBarComponent;
    topbarNewsComponent = TopBarNewsComponent;
    logoMobileSrc = 'assets/images/logo-dark.svg'; // TODO dark mode... /theme/images/logo-{{ isDarkTheme() ? 'white' : 'dark' }}.svg
    userName = '';
    topBarControls = TopBarControlsComponent;
    currentYear = new Date().getFullYear();
    menuBackLink: MenuItemType | undefined;
    private translations: AngularTranslations = {};
    private currentMenu?: MenuKey;
    private lastMainRoute?: string;
    private initMgr: InitializationManager;

    constructor(
        public readonly userService: UserService,
        private readonly layerService: LayerService,
        private readonly translate: TranslateService,
        private readonly layoutService: LayoutService,
        private readonly loadingService: LoadingService,
        private readonly titleService: Title,
        private readonly router: Router,
    ) {
        this.initMgr = new InitializationManager(this.loadingService, this.constructor.name);
        this.initMgr.loadingAnimation(false);
    }

    isDarkTheme = computed(() => this.layoutService.isDarkTheme());

    private getNavTranslationKeys(): string[] {
        return [
            'nav_overview',
            'nav_dashboard',
            'nav_analyze',
            'nav_summary',
            'nav_detailed',
            'nav_layer',
            'nav_manage',
            'nav_profile',
            'nav_user',
            'nav_settings',
            'nav_store',
            // settings menu:
            'nav_general',
            'nav_layer',
            'nav_integrations',
            'nav_api',
            // back link:
            'nav_back',
            // profile menu:
            'nav_profile',
            'nav_affiliate', // TODO or use nav_share ?
            'nav_apps',
            'nav_logout',
        ];
    }

    ngOnInit(): void {
        // reset the title to default; title was changed to 'timeBuzzer Login' in login.component.ts
        this.titleService.setTitle('timeBuzzer');

        this.initMgr
            .addDependency(this.userService.currentUser$, {
                name: 'currentUser$',
                callback: (user: CurrentUser) => {
                    this.currentUser = user;
                    this.userName = user.firstName + ' ' + user.lastName;
                },
            })
            .addDependency(this.layerService.layers$, {
                name: 'layers$',
                callback: (layers: Layer[]) => {
                    this.layers = layers;
                },
            })
            .addDependency(this.translate.stream(this.getNavTranslationKeys()), {
                name: 'translations',
                callback: (translations: AngularTranslations) => {
                    this.translations = translations;
                    this.profileMenuData = this.getProfileMenu(translations);
                },
            })
            .onInit(async ([_user, _layers, _translations]) => {
                this.subscriptions.add(
                    this.router.events
                        .pipe(
                            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                            startWith(null),
                        )
                        .subscribe((_nav: NavigationEnd | null) => {
                            // Note: main menu is the default to be shown when now specific menu is defined in the route.
                            const menuKey = this.getMenuKeyFromRoute() ?? 'main';
                            if (menuKey !== this.currentMenu) {
                                this.currentMenu = menuKey;
                                this.showMenu(menuKey);
                            }
                            // store last route for main menu - used for back link:
                            if (menuKey === 'main') {
                                // Note: router.url is the full serialized url including query string/fragment,
                                // which must not end up as a single routerLink path segment (it would not match
                                // any route and fall through to the notfound page) - so we strip them off (#728)
                                const urlTree = this.router.parseUrl(this.router.url);
                                urlTree.queryParams = {};
                                urlTree.fragment = null;
                                this.lastMainRoute = urlTree.toString();
                            }
                        }),
                );
            })
            .onChange(async ([_user, _layers, _trans]) => {
                this.showMenu(this.currentMenu ?? 'main');
            })
            .initialize();
    }

    private getMenuKeyFromRoute(): MenuKey | undefined {
        const rootRoute = this.router.routerState.snapshot.root;
        let route = rootRoute;
        let menuKey: MenuKey | undefined = undefined;
        while (route.firstChild) {
            route = route.firstChild;
            if (route.data['menuKey']) {
                menuKey = route.data['menuKey'];
            }
        }
        return menuKey;
    }

    private showMenu(menuKey: MenuKey): void {
        switch (menuKey) {
            case 'main':
                this.menuData = this.getMainMenu(this.currentUser!, this.layers!, this.translations);
                this.menuBackLink = undefined;
                break;
            case 'settings':
                this.menuData = this.getSettingsMenu(this.currentUser!, this.translations);
                this.menuBackLink = {
                    label: this.translations['nav_back'],
                    icon: 'pi pi-angle-left',
                    routerLink: [this.lastMainRoute ?? '/'],
                };
                break;
            default:
                console.error('Unexpected menuKey no menu definition found', menuKey);
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.initMgr.destroy();
    }

    private getProfileMenu(translations: AngularTranslations): MenuItemType[] {
        return [
            {
                label: translations['nav_profile'],
                icon: 'pi pi-user',
                routerLink: ['/profile'],
            },
            {
                label: translations['nav_apps'],
                icon: 'pi pi-download',
                routerLink: ['/apps'],
            },
            {
                label: translations['nav_affiliate'],
                icon: 'pi pi-heart',
                routerLink: ['/share'],
            },
            {
                label: translations['nav_logout'],
                icon: 'pi pi-power-off',
                routerLink: ['/logout'],
            },
        ];
    }

    private getMainMenu(user: CurrentUser, layers: Layer[], translations: AngularTranslations): MenuItemType[] {
        const isAdmin = user.isAdmin();
        const manageMenuItems: MenuItemEntry[] = [];
        // items for manage menu section:
        if (user.hasPermission(UserPermission.USER_READ)) {
            manageMenuItems.push({
                label: translations['nav_user'],
                icon: 'pi pi-fw pi-users',
                routerLink: ['/team'],
            });
        }
        if (isAdmin) {
            manageMenuItems.push({
                label: translations['nav_settings'],
                icon: 'pi pi-fw pi-cog',
                routerLink: ['/settings/general'],
                routeMatchPaths: 'subset',
            });
        }
        // menu items for layers:
        const layerMenuItems: MenuItemEntry[] = [];
        layers.forEach((layer) => {
            if (layer.visible) {
                const icon = 'pi-fw layer-icon layer-icon-0' + layer.index;
                layerMenuItems.push({ label: layer.name!, icon: icon, routerLink: ['/layer/' + layer.id] });
            }
        });

        const menu: MenuItemType[] = [
            {
                label: translations['nav_overview'],
                icon: 'pi pi-home',
                items: [{ label: translations['nav_dashboard'], icon: 'pi pi-fw pi-home', routerLink: ['/'] }],
            },
            { separator: true },
            {
                label: translations['nav_analyze'],
                icon: 'pi pi-home',
                items: [
                    {
                        label: translations['nav_summary'],
                        icon: 'pi pi-fw pi-chart-bar',
                        routerLink: ['/analyze/overview'],
                    },
                    {
                        label: translations['nav_detailed'],
                        icon: 'pi pi-fw pi-list',
                        routerLink: ['/analyze/detail'],
                    },
                ],
            },
            { separator: true },
            {
                label: translations['nav_layer'],
                icon: 'pi pi-angle-right',
                items: layerMenuItems,
            },
        ];
        // Note: regular users have no manage menu at all:
        if (manageMenuItems.length > 0) {
            menu.push({ separator: true });
            menu.push({
                label: translations['nav_manage'],
                icon: 'pi pi-home',
                items: manageMenuItems,
            });
        }

        return menu;
    }

    private getSettingsMenu(user: CurrentUser, translations: AngularTranslations): MenuItemType[] {
        if (!user.isAdmin()) {
            return [];
        }
        return [
            {
                label: translations['nav_settings'],
                icon: 'pi pi-cog',
                items: [
                    {
                        label: translations['nav_general'],
                        icon: 'pi pi-home',
                        routerLink: ['/settings/general'],
                    },
                    {
                        label: translations['nav_layer'],
                        icon: 'pi pi-sitemap',
                        routerLink: ['/settings/layer'],
                    },
                    {
                        label: translations['nav_integrations'],
                        icon: 'pi pi-tb-plug',
                        routerLink: ['/settings/integrations'],
                    },
                    {
                        label: translations['nav_api'],
                        icon: 'pi pi-tb-code',
                        routerLink: ['/settings/api'],
                    },
                    {
                        label: translations['nav_store'],
                        icon: 'pi pi-fw pi-credit-card',
                        routerLink: ['/settings/store'],
                    },
                    {
                        label: 'Hardware',
                        icon: 'pi pi-fw pi-shopping-cart',
                        routerLink: ['/settings/addons'],
                    },
                ],
            },
        ];
    }

    public async onLogoClick(): Promise<void> {
        await TtlCacheStore.refreshAll();
        void this.router.navigate(['/']);
    }
}
